import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const COLORS = ['#C00035', '#FFC107', '#E45680', '#FACC55', '#FFFFFF'];

interface Piece {
  left: number;
  xDrift: number;
  wobble: number;
  delay: number;
  duration: number;
  rotate: number;
  color: string;
  width: number;
  height: number;
  radius: number;
}

/**
 * ConfettiBurst
 * A subtle, premium one-shot confetti layer. Absolutely positioned and
 * pointer-events-none so it never interferes with the content beneath it.
 * Renders nothing when the user prefers reduced motion.
 */
const ConfettiBurst: React.FC<{ count?: number }> = ({ count = 44 }) => {
  const prefersReducedMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const [fallDistance, setFallDistance] = useState(0);

  const pieces = useMemo<Piece[]>(() => {
    return Array.from(
      { length: count },
      (): Piece => ({
        left: Math.random() * 100,
        xDrift: (Math.random() - 0.5) * 150,
        wobble: 12 + Math.random() * 28,
        delay: Math.random() * 0.9,
        duration: 3.2 + Math.random() * 1.4,
        rotate: (Math.random() > 0.5 ? 1 : -1) * (540 + Math.random() * 720),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        width: 5 + Math.random() * 5,
        height: 8 + Math.random() * 9,
        radius: Math.random() > 0.72 ? 999 : 1 + Math.random() * 3,
      }),
    );
  }, [count]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateDistance = () => setFallDistance(container.clientHeight + 48);
    updateDistance();

    const observer = new ResizeObserver(updateDistance);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  if (prefersReducedMotion) return null;

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
    >
      {fallDistance > 0 &&
        pieces.map((p, i) => (
          <motion.span
            key={i}
            initial={{ x: 0, y: -32, opacity: 0, rotate: 0 }}
            animate={{
              x: [0, p.wobble, -p.wobble * 0.45, p.wobble * 0.2, p.xDrift],
              y: fallDistance,
              opacity: [0, 1, 1, 1, 0],
              rotate: p.rotate,
            }}
            transition={{
              duration: p.duration,
              delay: p.delay,
              ease: [0.35, 0, 0.7, 1],
              times: [0, 0.08, 0.42, 0.9, 1],
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: `${p.left}%`,
              width: p.width,
              height: p.height,
              borderRadius: p.radius,
              backgroundColor: p.color,
              boxShadow:
                p.color === '#FFFFFF'
                  ? '0 0 0 1px rgba(0,0,0,0.06)'
                  : undefined,
              willChange: 'transform, opacity',
            }}
          />
        ))}
    </div>
  );
};

export default ConfettiBurst;
